import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import type { V2ListStartupCreditsResponse } from '@workspace/api-backend'
import { v2GetOutreachPricingOptions } from '@workspace/api-backend/react-query'
import { formatDate, PRICING } from '@workspace/seedforge-shared'
import { CtaButton } from '@workspace/ui/components/dsm/cta-button'
import { CreditCard, Gift, ShieldCheck, Wallet } from 'lucide-react'
import { useState } from 'react'
import {
  useBillingStatusQuery,
  useStartupCreditsQuery,
  useStartupWalletQuery,
  useWalletBillingPortalMutation,
} from '../../../hooks/api'
import { useBasePath } from '../../../hooks/use-base-path'
import { formatUsdCents } from '../../../lib/format'
import { OutreachTopUpDialog } from '../../outreach/components/outreach-topup-dialog'
import { RequestWithdrawalButton } from '../../outreach/components/request-withdrawal-button'
import { WALLET_SPEND_CAP_LINE } from '../../outreach/config/launch-checklist'
import { useOutreachStatusQuery } from '../../outreach/hooks/use-outreach-status'

// ─── Transaction history ────────────────────────────────────────────────────

type CreditTransaction = NonNullable<V2ListStartupCreditsResponse['data']>['transactions'][number]

// Human-readable label per transaction type. Legacy types fall through to the
// same buckets as their current equivalents so older rows still read cleanly.
const TYPE_LABELS: Record<CreditTransaction['type'], string> = {
  WALLET_TOPUP: 'Wallet top-up',
  OUTREACH_TOPUP: 'Wallet top-up',
  ADMIN_TOPUP: 'Wallet top-up',
  WALLET_CREDIT: 'Account credit',
  OUTREACH_REFUND: 'Refund',
  PITCH_SESSION_CHARGE: 'AI pitch session',
  PITCH_SESSION_USAGE: 'AI pitch session',
  EVALUATION_CHARGE: 'Fund evaluation',
  OUTREACH_SETTLEMENT: 'Outreach charges',
}

function formatTxDate(value: string): string {
  return new Date(value).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

// ─── Pricing rows ─────────────────────────────────────────────────────────────

// Session/evaluation prices are the shared pay-per-use constant. Outreach prices
// (view/return/call) come from the live pricing endpoint — see outreachRows below.
const PAY_PER_USE_ROWS = [
  {
    label: 'AI pitch session',
    // The first one is free for everyone, so the price alone misrepresents what
    // the founder is about to pay for their next session.
    price: `1 free, then $${PRICING.payPerUse.session} per AI Session`,
    to: '/pitch/start',
    description:
      'A live AI-powered pitch review that scores your startup and gives specific improvement recommendations.',
  },
  {
    label: 'Fund evaluation',
    price: `1 free, then $${PRICING.payPerUse.evaluation} per evaluation`,
    to: '/investors',
    description:
      'Submit your startup to specific funds for a formal evaluation of your fit with their investment thesis.',
  },
] as const

function PricingRow({
  label,
  price,
  description,
  freeNote,
  to,
}: {
  label: string
  price: string
  description: string
  /**
   * Replaces the price while the charge is waived. What the founder pays right
   * now is nothing, and that is the whole message; the price comes back on its
   * own the moment the trial ends.
   */
  freeNote?: string
  /**
   * Where the thing being charged for lives, relative to the startup. A price
   * on its own is a number to accept; linked, it is a number the founder can go
   * and see the other side of.
   */
  to?: string
}) {
  const basePath = useBasePath()
  const body = (
    <>
      <div>
        <p className="text-sm font-semibold text-white">{label}</p>
        <p className="mt-0.5 text-xs text-muted-foreground">{description}</p>
      </div>
      {freeNote ? (
        <span className="shrink-0 whitespace-nowrap rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-emerald-400">
          {freeNote}
        </span>
      ) : (
        <span className="shrink-0 whitespace-nowrap rounded-full border border-border bg-secondary/50 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          {price}
        </span>
      )}
    </>
  )

  if (!to) return <div className="flex items-start justify-between gap-4 px-5 py-4">{body}</div>

  return (
    <Link
      to={`${basePath}${to}`}
      className="flex items-start justify-between gap-4 px-5 py-4 transition-colors hover:bg-white/[0.02]"
    >
      {body}
    </Link>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function BillingPageContent({ startupId }: { startupId: string }) {
  const { data: billingData, isPending } = useBillingStatusQuery(startupId)
  const walletQuery = useStartupWalletQuery(startupId)
  const creditsQuery = useStartupCreditsQuery(startupId)
  const pricingQuery = useQuery(v2GetOutreachPricingOptions())
  const pricing = pricingQuery.data?.status === 'ok' ? pricingQuery.data.data : null
  const portal = useWalletBillingPortalMutation()
  const outreachStatus = useOutreachStatusQuery(startupId)
  const [topupOpen, setTopupOpen] = useState(false)

  // The free month starts when the campaign first goes live, so `trialEndsAt`
  // is null until then. Before that there is no date, but the promise still
  // holds and is the more useful thing to say: a founder deciding whether to
  // top up wants to know the first month is free, not that we cannot name the
  // day yet.
  const trialEndsAt = outreachStatus.data?.trialEndsAt ?? null
  const trialStarted = trialEndsAt !== null && new Date(trialEndsAt) > new Date()
  const trialDays = pricing?.trialDurationDays ?? null
  const trialPending = !trialStarted && trialEndsAt === null && trialDays !== null
  /** "until Aug 14, 2026" once running, "for your first 30 days" before. */
  const trialWindow = trialStarted
    ? `until ${formatDate(trialEndsAt)}`
    : trialPending
      ? `for your first ${trialDays} days`
      : null

  // Profile views and return visits are free, so they're omitted here. The two
  // outreach charges (intro call + warm intro) share the same price, so we
  // surface them as a single line at the top of the table.
  const outreachRows =
    pricing && (pricing.callBookedPriceCents > 0 || pricing.warmIntroPriceCents > 0)
      ? [
          {
            label: 'Intro call or warm intro',
            price: `${formatUsdCents(Math.max(pricing.callBookedPriceCents, pricing.warmIntroPriceCents))} each`,
            freeNote: trialWindow ? `Free ${trialWindow}` : undefined,
            to: '/outreach/replies',
            description:
              'Charged only when our team confirms an intro call was agreed, or a portfolio founder agrees to introduce you to your target investor. Profile views and return visits are always free.',
          },
        ]
      : []

  if (isPending) {
    return (
      <div className="mt-6">
        <p className="py-12 text-center text-sm text-muted-foreground">Loading...</p>
      </div>
    )
  }

  const billing = billingData?.data
  const wallet = walletQuery.data?.status === 'ok' ? walletQuery.data.data : null

  const promoSession = billing?.promoSession ?? false
  const prepaidSessions = (billing?.sessionsAvailable ?? 0) - (promoSession ? 1 : 0)
  const prepaidEvaluations = billing?.evaluationsAvailable ?? 0
  const canOpenPortal = (wallet?.lifetimeToppedUpCents ?? 0) > 0

  return (
    // No heading of its own: this is a tab under Startup settings, which
    // already says where the founder is. Width and padding come from that
    // layout too, so Billing lines up with General and Analytics.
    <div className="mt-6 space-y-8">
      {/* Wallet card */}
      <div className="rounded-2xl border border-border bg-card/50 p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-[#f97316]/10 text-[#f97316]">
              <Wallet className="size-5" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground">
                Wallet balance
              </p>
              {/* Always the number, including $0 — the balance is a fact about
                  the wallet. The trial sits beside it rather than replacing it,
                  so a $0 balance during the free month does not read as
                  "you cannot send". */}
              <div className="mt-1 flex flex-wrap items-center gap-2.5">
                <p className="text-3xl font-bold tabular-nums text-white">
                  {formatUsdCents(wallet?.balanceCents ?? 0)}
                </p>
                {trialWindow && (
                  <span className="whitespace-nowrap rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-emerald-400">
                    Free trial
                  </span>
                )}
              </div>
              {wallet && wallet.pendingAccrualsCents > 0 && (
                <p className="mt-1 text-xs text-muted-foreground">
                  Pending outreach charges: −{formatUsdCents(wallet.pendingAccrualsCents)}
                </p>
              )}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <RequestWithdrawalButton
              startupId={startupId}
              balanceCents={wallet?.balanceCents ?? 0}
            />
            {canOpenPortal && (
              <div className="flex flex-col items-start gap-1">
                <button
                  type="button"
                  onClick={() =>
                    portal.mutate(
                      { path: { id: startupId } },
                      {
                        onSuccess: (response) => {
                          if (response.status === 'ok' && response.data.portalUrl) {
                            window.location.assign(response.data.portalUrl)
                          }
                        },
                      }
                    )
                  }
                  disabled={portal.isPending}
                  className="inline-flex items-center gap-1.5 rounded-xl border border-border bg-secondary/50 px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:text-white disabled:opacity-50"
                >
                  <CreditCard className="size-4" />
                  Manage card
                </button>
                {portal.isError && (
                  <p className="text-[13px] text-red-400">
                    Couldn't open the billing portal. Please try again.
                  </p>
                )}
              </div>
            )}
            <CtaButton icon={<Wallet />} onClick={() => setTopupOpen(true)}>
              Top up
            </CtaButton>
          </div>
        </div>

        {wallet && (
          <div className="mt-5 border-t border-border pt-4">
            <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-muted-foreground">
              <span>Total topped up: {formatUsdCents(wallet.lifetimeToppedUpCents)}</span>
              <span>Total spent: {formatUsdCents(wallet.lifetimeSpentCents)}</span>
            </div>
            <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
              {WALLET_SPEND_CAP_LINE}
            </p>
          </div>
        )}
      </div>

      {/* Free trial */}
      {trialWindow && (
        <div className="flex items-center gap-3 rounded-2xl border border-emerald-500/30 bg-emerald-500/5 px-5 py-4">
          <div className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-emerald-500/10">
            <ShieldCheck className="size-4.5 text-emerald-400" />
          </div>
          <div>
            <p className="text-sm font-semibold text-emerald-400">Free trial {trialWindow}</p>
            <p className="text-xs text-muted-foreground">
              {trialStarted
                ? 'Outreach charges are waived until then. Your campaign keeps running afterwards as long as your wallet has a balance.'
                : 'Outreach charges are waived for the first month. The clock starts when your campaign goes live, not today.'}
            </p>
          </div>
        </div>
      )}

      {/* Free session banner */}
      {promoSession && (
        <div className="flex items-center gap-3 rounded-2xl border border-[#f97316]/20 bg-[#f97316]/5 px-5 py-4">
          <div className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-[#f97316]/10">
            <Gift className="size-4.5 text-[#f97316]" />
          </div>
          <div>
            <p className="text-sm font-semibold text-white">You have 1 free pitch session</p>
            <p className="text-xs text-muted-foreground">
              Your first AI pitch session is on us. Start it from the Feedback page.
            </p>
          </div>
        </div>
      )}

      {/* Prepaid units */}
      {(prepaidSessions > 0 || prepaidEvaluations > 0) && (
        <div className="rounded-2xl border border-border bg-card/50 px-5 py-4">
          <p className="text-sm font-semibold text-white">Prepaid balance</p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {[
              prepaidSessions > 0
                ? `${prepaidSessions} prepaid session${prepaidSessions === 1 ? '' : 's'}`
                : null,
              prepaidEvaluations > 0
                ? `${prepaidEvaluations} prepaid evaluation${prepaidEvaluations === 1 ? '' : 's'}`
                : null,
            ]
              .filter(Boolean)
              .join(' and ')}{' '}
            remaining, used before your wallet is charged.
          </p>
        </div>
      )}

      {/* Pricing list */}
      <div className="space-y-3">
        <h2 className="text-sm font-semibold uppercase tracking-[0.1em] text-muted-foreground">
          Pay as you go
        </h2>
        <div className="divide-y divide-border rounded-2xl border border-border bg-card/50">
          {[...outreachRows, ...PAY_PER_USE_ROWS].map((row) => (
            <PricingRow key={row.label} {...row} />
          ))}
        </div>
      </div>

      {/* Transaction history */}
      <div className="space-y-3">
        <h2 className="text-sm font-semibold uppercase tracking-[0.1em] text-muted-foreground">
          Transaction history
        </h2>
        {creditsQuery.isPending ? (
          <p className="py-6 text-center text-sm text-muted-foreground">Loading...</p>
        ) : creditsQuery.isError ? (
          <div className="rounded-2xl border border-border bg-card/50 px-5 py-8 text-center text-sm text-muted-foreground">
            Couldn't load your transactions. Please try again.
          </div>
        ) : (creditsQuery.data?.transactions.length ?? 0) === 0 ? (
          <div className="rounded-2xl border border-dashed border-border/50 px-5 py-8 text-center text-sm text-muted-foreground">
            No transactions yet.
          </div>
        ) : (
          <div className="divide-y divide-border rounded-2xl border border-border bg-card/50">
            {creditsQuery.data?.transactions.map((tx) => {
              const isCredit = tx.amount > 0
              return (
                <div key={tx.id} className="flex items-center justify-between gap-4 px-5 py-3.5">
                  <div>
                    <p className="text-sm font-medium text-white">
                      {tx.label ?? TYPE_LABELS[tx.type]}
                    </p>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {formatTxDate(tx.createdAt)}
                    </p>
                  </div>
                  <span
                    className={`shrink-0 text-sm font-semibold tabular-nums ${
                      isCredit ? 'text-emerald-400' : 'text-muted-foreground'
                    }`}
                  >
                    {isCredit ? '+' : ''}
                    {formatUsdCents(tx.amount)}
                  </span>
                </div>
              )
            })}
          </div>
        )}
      </div>

      <OutreachTopUpDialog
        startupId={startupId}
        open={topupOpen}
        onOpenChange={setTopupOpen}
        returnTo="billing"
        activateOutreach={false}
      />
    </div>
  )
}
